<template>
  <q-table
    flat
    ref="tableRef"
    :rows="rows"
    :columns="columns"
    row-key="id"
    :loading="loading"
    v-model:pagination="pagination"
    @request="onRequest"
    binary-state-sort
    class="external-dns-table"
    :rows-per-page-options="rowsPerPageOptions"
  >
    <template v-slot:no-data>
      <div class="flex full-width column items-center q-py-lg">
        <q-icon name="mdi-cloud-search" class="q-mb-sm" size="40px" color="primary"></q-icon>
        <span class="block" style="font-size: 14px">No items were found.</span>
      </div>
    </template>
    <template v-slot:body-cell-id="props">
      <q-td :props="props" align="left">
        <span class="flex items-center"
          ><q-icon v-if="props.row.deletedAt" size="18px" color="grey" name="close"></q-icon>{{ props.row.id }}</span
        >
      </q-td>
    </template>
    <template v-slot:body-cell-actions="props">
      <q-td :props="props">
        <WorkflowsBtn
          @showWorkflow="showWorkflow($event, props.row)"
          :loading="loading"
          :relatedWorkflowStatuses="relatedWorkflowStatuses.find((item) => item.uuid === props.row.uuid)?.statuses"
        />
        <q-btn
          flat
          round
          size="9px"
          style="width: 30px; height: 40px"
          class="btn--no-hover"
          :ripple="false"
          icon="engp:engp-ico-copy"
          @click.stop="copyId(props.row.uuid)"
        >
          <q-tooltip>Copy UUID</q-tooltip>
        </q-btn>
        <q-btn
          v-if="!props.row.deletedAt"
          flat
          round
          size="12px"
          icon="sym_r_edit"
          style="width: 30px; height: 40px"
          class="btn--no-hover"
          :loading="loading"
          :disabled="loading || disableEditIds.find((item) => item.id === props.row.id)"
          @click="$router.push(`/service-schemas/${encodeURIComponent(props.row.uuid)}`)"
        >
          <q-tooltip v-if="!loading" max-width="480px" style="white-space: pre-line">{{
            !!disableEditIds.find((item) => item.id === props.row.id)
              ? disableEditIds.find((item) => item.id === props.row.id)?.message
              : 'Edit'
          }}</q-tooltip>
        </q-btn>
        <q-btn
          v-if="!props.row.deletedAt"
          flat
          round
          size="12px"
          @click="$emit('confirmDeleteSchema', props.row)"
          :loading="loading"
          :disabled="loading || disableDeleteIds.find((item) => item.id === props.row.id)"
          icon="sym_r_delete"
          style="width: 30px; height: 40px"
          class="btn--no-hover"
        >
          <q-tooltip max-width="480px" style="white-space: pre-line" v-if="!loading">{{
            disableDeleteIds.find((item) => item.id === props.row.id)
              ? disableDeleteIds.find((item) => item.id === props.row.id).message
              : 'Delete'
          }}</q-tooltip>
        </q-btn>
      </q-td>
    </template>
  </q-table>
</template>

<script>
import dayjs from 'dayjs';
import { copyToClipboard } from 'quasar';
import { getServiceSchemas } from '../api/api';
import WorkflowsBtn from '@/shared/components/WorkflowsBtn.vue';
import { supportCntrlClick } from '@/shared/helpers';
import { validateWorkFlow, getRelatedWorkflowsStatuses } from '@/pages/registrations/api/api';

export default {
  name: 'ServiceSchemasTable',
  components: {
    WorkflowsBtn,
  },
  props: {
    filters: {
      type: Object,
      default: () => ({}),
    },
    rowsPerPageOptions: {
      type: Array,
      default: () => [20, 50, 100],
    },
  },
  emits: ['confirmDeleteSchema'],
  data() {
    return {
      rows: [],
      loading: false,
      disableEditIds: [],
      disableDeleteIds: [],
      relatedWorkflowStatuses: [],
      pagination: {
        sortBy: 'updatedAt',
        descending: true,
        page: 1,
        rowsPerPage: 20,
        rowsNumber: 0,
      },
      columns: [
        {
          name: 'id',
          label: 'ID',
          field: 'id',
          align: 'left',
          sortable: true,
        },
        {
          label: 'Created at',
          name: 'createdAt',
          field: 'createdAt',
          align: 'left',
          format: (val) => dayjs(val).format('DD.MM.YYYY HH:mm'),
          sortable: true,
        },
        {
          name: 'updatedAt',
          label: 'Updated At',
          field: 'updatedAt',
          align: 'left',
          format: (val) => dayjs(val).format('DD.MM.YYYY HH:mm'),
          sortable: true,
        },
        {
          name: 'actions',
          label: '',
          field: 'actions',
          align: 'right',
          sortable: false,
        },
      ],
    };
  },
  methods: {
    showWorkflow(event, data) {
      supportCntrlClick(event, `/workflows?itemId=${data.uuid}`);
    },
    async onRequest(props) {
      const { page, rowsPerPage, sortBy, descending } = props.pagination;

      this.loading = true;

      try {
        this.relatedWorkflowStatuses = [];
        const skip = page === 1 ? 0 : (page - 1) * rowsPerPage;
        const { count, data } = await getServiceSchemas(
          rowsPerPage,
          skip,
          sortBy,
          descending,
          this.filters.dateFrom,
          this.filters.dateTo,
          this.filters.deleted,
          this.filters.id,
          this.filters.uuid,
          this.filters.schema,
        );
        this.rows = data;
        this.pagination = { ...props.pagination, rowsNumber: count };
        this.$router.replace({ query: this.buildQueryFromFilters() });

        const deletePrevalPromises = this.rows.map(async (item) => {
          const data = {
            workflowId: 'engprt.delete-service-schema',
            inputVariables: {
              id: item.id,
              uuid: item.uuid,
            },
          };
          return validateWorkFlow(data).catch((res) => {
            this.disableDeleteIds.push({ id: item.id, message: res.message });
          });
        });

        // const editPrevalPromises = this.rows.map(async (item) => {
        //   const data = {
        //     workflowId: 'engprt.update-service-schema',
        //     inputVariables: {
        //       uuid: item.uuid,
        //     },
        //   };
        //   return validateWorkFlow(data).catch((res) => {
        //     this.disableEditIds.push({ id: item.id, message: res.message });
        //   });
        // });
        const relatedWorkflowsPromises = this.rows.map((record) => {
          return getRelatedWorkflowsStatuses(record.uuid).then((response) => {
            this.relatedWorkflowStatuses.push({ uuid: record.uuid, statuses: response });
          });
        });
        await Promise.all([...deletePrevalPromises, ...relatedWorkflowsPromises]);
      } catch (error) {
        this.$notify('negative', 'Error', error.message);
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
    buildQueryFromFilters() {
      const query = {};
      for (const key in this.filters) {
        if (this.filters[key] !== undefined && this.filters[key] !== null && this.filters[key] !== '') {
          query[key] = String(this.filters[key]);
        }
      }
      if (this.pagination.sortBy) {
        query.sortBy = this.pagination.sortBy;
        query.descending = String(this.pagination.descending);
      }
      query.page = String(this.pagination.page);
      query.rowsPerPage = String(this.pagination.rowsPerPage);
      return query;
    },
    reload() {
      this.onRequest({ pagination: this.pagination });
    },
    copyId(uuid) {
      copyToClipboard(uuid)
        .then(() => {
          this.$notify('positive', 'Copied', 'UUID copied to clipboard');
        })
        .catch(() => {
          this.$notify('negative', 'Error', 'Failed to copy UUID');
        });
    },
    confirmDelete(row) {
      this.$emit('confirmDeleteSchema', row);
    },
  },
  mounted() {
    const query = this.$route.query;
    if (query.sortBy) this.pagination.sortBy = query.sortBy;
    if (query.descending) this.pagination.descending = query.descending === 'true';
    if (query.page) this.pagination.page = Number(query.page);
    if (query.rowsPerPage) this.pagination.rowsPerPage = Number(query.rowsPerPage);
  },
};
</script>

<style lang="scss" scoped>
.long-desc {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

.sticky-header-table {
  max-height: 100%;

  :deep(thead tr th) {
    position: sticky;
    z-index: 1;
    background: white;
  }

  :deep(thead tr:first-child th) {
    top: 0;
  }
}
</style>
